import React from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/logo_basic.png";
import AppFooter from "../components/AppFooter";
import SEO from "../components/SEO";
import { useAuth } from "../context/AuthProvider";

import { createProductSchema } from "../seo/schemas/productSchema";
import { createBreadcrumbSchema } from "../seo/schemas/breadcrumbSchema";

const SITE_URL = "https://www.reignitespirit.com";
const PAGE_PATH = "/deep-audit";
const PAGE_URL = `${SITE_URL}${PAGE_PATH}`;

export default function DeepAuditPage() {
  const navigate = useNavigate();
  const { session } = useAuth();

  const deepAuditProductSchema = createProductSchema({
    id: `${PAGE_URL}#product`,
    name: "The Deep Audit",
    description:
      "A detailed AI-assisted career transition analysis covering career fit, transferable skills, AI-related role risks, career pathways, skill gaps, resume readiness and practical action planning.",
    image: `${SITE_URL}/og-career-audit.jpeg`,
    url: PAGE_URL,
    price: "0.00",
    currency: "USD",
    category: "Career Intelligence Report",
  });

  const breadcrumbSchema = createBreadcrumbSchema({
    pageUrl: PAGE_URL,
    items: [
      {
        name: "Home",
        url: `${SITE_URL}/landing`,
      },
      {
        name: "The Deep Audit",
        url: PAGE_URL,
      },
    ],
  });

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${PAGE_URL}#webpage`,
    url: PAGE_URL,
    name: "The Deep Audit",
    description:
      "A detailed AI-assisted career transition report covering role impact, transferable strengths, multiple career pathways, skill gaps, resume readiness and practical next steps.",
    isPartOf: {
      "@id": `${SITE_URL}/#website`,
    },
    about: {
      "@id": `${PAGE_URL}#product`,
    },
    breadcrumb: {
      "@id": `${PAGE_URL}#breadcrumb`,
    },
    inLanguage: "en",
  };

  const goToRegister = () => {
    localStorage.setItem("selectedPlan", "deep_audit");
    navigate("/register?plan=deep_audit");
  };

  return (
    <div className="deep-audit-page min-h-screen bg-[#11111f] text-slate-300 font-sans antialiased">
      <SEO
        title="The Deep Audit"
        description="Get a detailed AI-assisted career transition analysis covering role impact, transferable strengths, multiple career pathways, skill gaps, resume readiness and practical action planning."
        path={PAGE_PATH}
        image="/og-career-audit.jpeg"
        type="product"
        author="ReigniteSpirit"
        keywords="deep career audit, AI career assessment, career transition report, AI job impact analysis, career pathway report, skill gap analysis, ATS resume analysis"
        robots="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        schema={[
          webPageSchema,
          deepAuditProductSchema,
          breadcrumbSchema,
        ]}
      />

      {/* =====================================================
          MAIN WEBSITE HEADER
      ===================================================== */}
      <header className="sticky top-0 z-50 border-b border-slate-800 bg-[#11111f]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 gap-4">

            {/* LANDING-STYLE LOGO / BRAND */}
            <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
              <button
                type="button"
                onClick={() => navigate("/landing")}
                className="flex items-center gap-3 cursor-pointer min-w-0 text-left"
                aria-label="Go to Reignite Spirit home page"
              >
                <img
                  src={logo}
                  alt="Reignite Spirit Logo"
                  className="w-10 sm:w-12 h-10 sm:h-12 drop-shadow-[0_0_14px_#eb6fbd] flex-shrink-0"
                  width="48"
                  height="48"
                  loading="eager"
                  decoding="async"
                />

                <div className="min-w-[190px]">
                  <h1 className="text-base sm:text-lg font-semibold tracking-wide text-[#EB6AB5] whitespace-nowrap">
                    REIGNITE.SPIRIT
                  </h1>

                  <p className="text-[10px] sm:text-xs text-[#EB6AB5]/80 tracking-[0.15em] uppercase whitespace-nowrap">
                    Rekindle Purpose
                  </p>
                </div>
              </button>
            </div>

            {/* DESKTOP NAVIGATION */}
            <nav className="hidden lg:flex items-center gap-5 text-sm font-sans">
              <Link
                to="/landing"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Home
              </Link>

              <Link
                to="/snapshot"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Snapshot
              </Link>

              <Link
                to="/deep-audit"
                className="text-[#EB6AB5] font-semibold"
              >
                Deep Audit
              </Link>

              <Link
                to="/blog"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Career Intelligence
              </Link>

              <Link
                to="/faq"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                FAQ
              </Link>

              {session ? (
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="px-4 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="px-3 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
                  >
                    Login
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/register")}
                    className="px-3 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition whitespace-nowrap"
                  >
                    Register
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/choose-plan")}
                    className="px-3 py-2 rounded-lg bg-[#cb01bd] hover:bg-[#e94ce0] text-white font-bold transition whitespace-nowrap"
                  >
                    Get My Audit
                  </button>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* =====================================================
          PAGE CONTENT
      ===================================================== */}
      <main className="deep-audit-main">
        <div className="w-full max-w-5xl mx-auto px-4 pt-12 pb-16 md:pt-16 md:pb-20">

          {/* BREADCRUMB */}
          <nav
            aria-label="Breadcrumb"
            className="text-sm text-slate-400 mb-10"
          >
            <Link
              to="/landing"
              className="hover:text-[#EB6AB5] transition"
            >
              Home
            </Link>

            <span className="mx-2" aria-hidden="true">
              /
            </span>

            <span aria-current="page">
              The Deep Audit
            </span>
          </nav>

          {/* HERO */}
          <header className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full bg-[#fbbf24]/10 text-[#fbbf24] font-bold text-sm mb-4">
              RECOMMENDED
            </span>

            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6">
              The Deep Audit
            </h1>

            <p className="max-w-3xl mx-auto text-lg text-slate-400 leading-relaxed">
              A comprehensive AI-assisted career resilience toolkit for
              professionals who need a deeper and more structured transition
              roadmap in a changing job market.
            </p>
          </header>

          {/* =====================================================
              DEEP AUDIT OFFER
          ===================================================== */}
          <section
            className="glass-card rounded-3xl border border-[#fbbf24] p-8 md:p-10 bg-[#1a1a2e]"
            aria-labelledby="deep-audit-offer-heading"
          >
            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div>
                <h2
                  id="deep-audit-offer-heading"
                  className="text-2xl font-bold text-white mb-4"
                >
                  Deep Career Audit Report
                </h2>

                <div className="mb-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-4xl md:text-5xl font-extrabold text-slate-400 line-through decoration-red-500 decoration-4">
                      US$19.99
                    </span>

                    <span className="text-base text-slate-500 font-normal">
                      one-time
                    </span>
                  </div>

                  <div className="text-5xl font-extrabold text-emerald-400 mt-3">
                    FREE
                  </div>

                  <p className="text-[#fbbf24] font-bold mt-2">
                    Launch Special — Limited Time
                  </p>
                </div>

                <p className="text-slate-400 mb-8 leading-relaxed">
                  A detailed toolkit designed to help you evaluate career
                  risks, identify practical pathways and plan your next steps.
                </p>

                <button
                  type="button"
                  onClick={goToRegister}
                  className="w-full md:w-auto px-8 py-4 bg-[#fbbf24] text-black font-bold rounded-xl hover:bg-yellow-400 transition"
                >
                  Start Deep Audit →
                </button>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-6">
                  What you get
                </h2>

                <ul className="space-y-4">
                  <li className="text-slate-300">
                    ✓ AI-related current job impact assessment
                  </li>

                  <li className="text-slate-300">
                    ✓ Transferable and distinctive skills assessment
                  </li>

                  <li className="text-slate-300">
                    ✓ Three suggested AI-resilient career pathways relevant
                    to your selected location
                  </li>

                  <li className="text-slate-300">
                    ✓ Skill-gap assessment and suggested reskilling areas
                  </li>

                  <li className="text-slate-300">
                    ✓ Career-interest profiling based on the methodology
                    currently supported by the platform
                  </li>

                  <li className="text-slate-300">
                    ✓ Resume analysis with ATS-readiness guidance
                  </li>

                  <li className="text-slate-300">
                    ✓ Practical career transition action plan
                  </li>

                  <li className="text-slate-300">
                    ✓ Access to eligible supporting tools and future features
                    included with the plan
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* =====================================================
              DETAILS
          ===================================================== */}
          <section
            className="mt-12 grid md:grid-cols-3 gap-6"
            aria-labelledby="deep-audit-details-heading"
          >
            <h2
              id="deep-audit-details-heading"
              className="sr-only"
            >
              Deep Audit report details
            </h2>

            <article className="rounded-2xl border border-slate-700 bg-[#1a1a2e] p-6">
              <h3 className="text-xl font-bold text-white mb-3">
                Who it is for
              </h3>

              <p className="text-slate-400 leading-relaxed">
                Professionals who need a detailed career transition analysis
                rather than a quick initial assessment.
              </p>
            </article>

            <article className="rounded-2xl border border-slate-700 bg-[#1a1a2e] p-6">
              <h3 className="text-xl font-bold text-white mb-3">
                What it helps clarify
              </h3>

              <p className="text-slate-400 leading-relaxed">
                Potential role disruption, transferable strengths, possible
                career directions, learning priorities and resume readiness.
              </p>
            </article>

            <article className="rounded-2xl border border-slate-700 bg-[#1a1a2e] p-6">
              <h3 className="text-xl font-bold text-white mb-3">
                Important limitation
              </h3>

              <p className="text-slate-400 leading-relaxed">
                The report is an informational career-planning aid. It does
                not guarantee employment, salary, promotion, admission to a
                course or a particular career outcome.
              </p>
            </article>
          </section>

          {/* =====================================================
              SNAPSHOT COMPARISON
          ===================================================== */}
          <section
            className="mt-12 rounded-3xl border border-[#fbbf24]/30 bg-[#1a1a2e] p-8"
            aria-labelledby="snapshot-comparison-heading"
          >
            <h2
              id="snapshot-comparison-heading"
              className="text-2xl font-bold text-[#fbbf24] mb-4"
            >
              Deep Audit compared with Snapshot
            </h2>

            <p className="text-slate-400 leading-relaxed mb-6">
              The Snapshot is designed for quick initial clarity. The Deep
              Audit provides broader career-pathway analysis, more detailed
              skill-gap guidance, resume readiness assessment and a more
              structured transition plan.
            </p>

            <Link
              to="/snapshot"
              className="inline-flex px-5 py-3 rounded-xl border border-[#fbbf24] text-[#fbbf24] font-bold hover:bg-[#fbbf24] hover:text-black transition"
            >
              Compare with The Snapshot
            </Link>
          </section>

          {/* =====================================================
              RELATED RESOURCES
          ===================================================== */}
          <section
            className="mt-12 rounded-3xl border border-[#EB6AB5]/20 bg-[#1a1a2e] p-8"
            aria-labelledby="deep-audit-related-resources"
          >
            <h2
              id="deep-audit-related-resources"
              className="text-2xl font-bold text-[#EB6AB5] mb-6"
            >
              Related career planning resources
            </h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/methodology"
                className="block rounded-xl border border-slate-700 p-5 hover:border-[#EB6AB5] transition"
              >
                <h3 className="font-bold text-white mb-2">
                  Our Methodology
                </h3>

                <p className="text-sm text-slate-400 leading-relaxed">
                  Review how career risks, transferable skills, pathways and
                  transition readiness are evaluated.
                </p>
              </Link>

              <Link
                to="/data-sources"
                className="block rounded-xl border border-slate-700 p-5 hover:border-[#EB6AB5] transition"
              >
                <h3 className="font-bold text-white mb-2">
                  Career Intelligence Data Sources
                </h3>

                <p className="text-sm text-slate-400 leading-relaxed">
                  Understand the occupational, labour-market and analytical
                  information considered by the platform.
                </p>
              </Link>

              <Link
                to="/runway-calculator"
                className="block rounded-xl border border-slate-700 p-5 hover:border-[#EB6AB5] transition"
              >
                <h3 className="font-bold text-white mb-2">
                  Financial Runway Calculator
                </h3>

                <p className="text-sm text-slate-400 leading-relaxed">
                  Estimate how long your financial resources may support your
                  career transition plan.
                </p>
              </Link>

              <Link
                to="/disclaimer"
                className="block rounded-xl border border-slate-700 p-5 hover:border-[#EB6AB5] transition"
              >
                <h3 className="font-bold text-white mb-2">
                  Platform Disclaimer
                </h3>

                <p className="text-sm text-slate-400 leading-relaxed">
                  Review the limitations that apply to career reports,
                  automated outputs and financial estimates.
                </p>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* =====================================================
          FOOTER
      ===================================================== */}
      <AppFooter
        variant="landing"
        logoSrc={logo}
      />
    </div>
  );
}